import React, { useId, useState } from "react";
import { Dialog } from "../ui/Dialog";
import { X, ChevronDown } from "lucide-react";

interface AddListDialogProps {
  newListName: string;
  setNewListName: (name: string) => void;
  newListEmoji: string;
  setNewListEmoji: (emoji: string) => void;
  onClose: () => void;
  onAdd: () => void;
}

const COMMON_EMOJIS = [
  "📁", "📂", "📋", "📝", "💼", "🏠", "🛒", "💰", "📚", "💪",
  "🎯", "⭐", "❤️", "🔥", "✨", "🎉", "🚀", "💡", "🎨", "🎵",
  "🏃", "🍎", "☕", "🌟", "📱", "💻", "🔧", "🎮", "📷", "✈️",
];

const AddListDialog: React.FC<AddListDialogProps> = ({
  newListName,
  setNewListName,
  newListEmoji,
  setNewListEmoji,
  onClose,
  onAdd,
}) => {
  const titleId = useId();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  return (
    <Dialog
      onClose={onClose}
      labelledBy={titleId}
      backdropClassName="z-40"
      panelClassName="w-full max-w-md max-h-[95vh] overflow-y-auto"
      closeOnBackdropClick={false}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 id={titleId} className="text-lg font-semibold text-warm-text-primary">Add New List</h3>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-warm-text-secondary hover:text-warm-text-primary transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-1">
            Emoji
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="w-full bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-2 text-2xl text-center focus:outline-none focus:ring-2 focus:ring-warm-blue flex items-center justify-center gap-2"
            >
              <span>{newListEmoji}</span>
              <ChevronDown size={16} className="text-warm-text-tertiary" />
            </button>
            {showEmojiPicker && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-warm-bg-card border border-warm-border rounded-lg p-2 z-10 shadow-lg">
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-1">
                  {COMMON_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setNewListEmoji(emoji);
                        setShowEmojiPicker(false);
                      }}
                      className={`p-1.5 text-xl rounded hover:bg-warm-border-subtle transition-colors ${
                        newListEmoji === emoji ? "bg-warm-border-subtle" : ""
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-warm-border">
                  <input
                    type="text"
                    value={newListEmoji}
                    onChange={(e) => setNewListEmoji(e.target.value)}
                    placeholder="Or paste emoji here..."
                    className="w-full bg-warm-bg-elevated border border-warm-border rounded px-2 py-1 text-sm text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-1 focus:ring-warm-blue"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-warm-text-secondary mb-1">
            List Name
          </label>
          <input
            type="text"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && onAdd()}
            placeholder="e.g., Shopping, Errands, Projects"
            className="w-full bg-warm-bg-elevated border border-warm-border rounded-lg px-3 py-2 text-sm text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
            autoFocus
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onAdd}
            className="flex-1 px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors"
          >
            Add List
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddListDialog;
